import { t } from 'i18next'
import { FC, ReactNode, useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'

import { EventAvailable } from '@mui/icons-material'
import { Button, ButtonProps, Fab } from '@mui/material'

import { GRTooltip } from '../../../../components/GRTooltip/GRTooltip'
import { useAppDispatch, useAppSelector } from '../../../../hooks/storeHooks'
import { modalParamPrefix, modalTypeParamKey } from '../../../route-modals/const/const'
import { highlightEvent, selectLiveEventModalState } from '../../slices/liveEventModalSlice'
import { LiveEventTrackerTab } from '../../types/LiveEventTrackerTab'

/**
 * Button component for populating related redux state for the LiveEventsCalendar event highlighting
 */
type EventHighlightButtonProps = Omit<ButtonProps, 'variant'> & {
  eventId: string
  trackedGameId?: string
  eventStartTime?: number
  eventEndTime?: number
  eventType?: string
  onEventHighlighted?: (eventId: string) => void
  variant?: 'normal' | 'fab'
  children?: ReactNode
}

export const EventHighlightButton: FC<EventHighlightButtonProps> = ({
  eventId,
  trackedGameId,
  eventStartTime,
  eventEndTime,
  eventType,
  onEventHighlighted,
  variant = 'normal',
  children,
  ...buttonProps
}) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  // Fall back to m_trackedGameId from URL
  const resolvedTrackedGameId = trackedGameId || new URLSearchParams(window.location.search).get(`${modalParamPrefix}trackedGameId`) || undefined
  const stripModalParams = (search: string) => {
    const params = new URLSearchParams(search)
    const keysToDelete = [modalTypeParamKey, ...Array.from(params.keys()).filter((key) => key.startsWith(modalParamPrefix))]
    keysToDelete.forEach((key) => params.delete(key))
    return params
  }
  const liveEventModalState = useAppSelector(selectLiveEventModalState)
  const highlightedEventId = liveEventModalState?.id
  const isSameAsCurrentSelection = eventId === highlightedEventId

  const highlightEventClick = useCallback(
    (eventId: string) => {
      const liveEventsTrackerOpen = location.pathname.includes('/live-events-tracker')
      const gameInSearchParams = !resolvedTrackedGameId || new URLSearchParams(window.location.search).getAll('gameIds').includes(resolvedTrackedGameId)
      const needsGameAddedToUrl = liveEventsTrackerOpen && !gameInSearchParams && !!resolvedTrackedGameId

      dispatch(
        highlightEvent(
          isSameAsCurrentSelection
            ? {}
            : {
                id: eventId,
                meta: {
                  trackedGameId: resolvedTrackedGameId,
                  eventStartTime,
                  eventEndTime,
                  eventType,
                  timeout: liveEventsTrackerOpen && !needsGameAddedToUrl ? 0 : 1000,
                },
              }
        )
      )

      if (isSameAsCurrentSelection) return

      // Use window.location.search to get the current live search params (avoids stale closure)
      const currentSearch = window.location.search

      // Preserve the current Live Events Tracker tab (Games / Compare Events) when the user
      // already has the tracker open. Outside the tracker, default to the Games tab.
      const currentTrackerTabMatch = location.pathname.match(/\/live-events-tracker\/([^/?#]+)/)
      const currentTrackerTab = currentTrackerTabMatch?.[1]
      const targetTab =
        liveEventsTrackerOpen && (currentTrackerTab === LiveEventTrackerTab.Games || currentTrackerTab === LiveEventTrackerTab.Events)
          ? currentTrackerTab
          : LiveEventTrackerTab.Games

      if (!liveEventsTrackerOpen) {
        // Navigate without gameIds — LET will restore saved games from settings,
        // and the layout effect will add the highlighted game on top of them
        navigate(`/live-events-tracker/${targetTab}`)
      } else if (needsGameAddedToUrl) {
        const cleanedParams = stripModalParams(currentSearch)
        // Collect all existing gameIds and add the new one
        const existingGameIds = cleanedParams.getAll('gameIds').flatMap((v) => v.split(','))
        cleanedParams.delete('gameIds')
        cleanedParams.set('gameIds', [...existingGameIds, resolvedTrackedGameId].join(','))
        navigate(`/live-events-tracker/${targetTab}?${cleanedParams.toString()}`)
      } else {
        const cleanedParams = stripModalParams(currentSearch)
        navigate(`/live-events-tracker/${targetTab}?${cleanedParams.toString()}`)
        onEventHighlighted?.(eventId)
      }
    },
    [location.pathname, resolvedTrackedGameId, dispatch, isSameAsCurrentSelection, eventStartTime, eventEndTime, eventType, navigate, onEventHighlighted]
  )

  const isHiglightedEvent = highlightedEventId === eventId
  const buttonColor = isHiglightedEvent ? 'secondary' : 'success'
  const text = isHiglightedEvent
    ? t('live-events:highlight_these_events_in_calendar_button_remove')
    : t('live-events:highlight_these_events_in_calendar_button')

  return variant === 'normal' ? (
    <Button variant="contained" color={buttonColor} startIcon={<EventAvailable />} onClick={() => highlightEventClick(eventId)} {...buttonProps}>
      {text}
    </Button>
  ) : variant === 'fab' ? (
    <GRTooltip content={text}>
      <Fab
        variant="circular"
        size="small"
        sx={{
          width: 24,
          height: 24,
          minHeight: 24,
          boxShadow: (theme) => theme.shadows[2],
          backgroundColor: (theme) => (isHiglightedEvent ? theme.palette.secondary.main : theme.palette.success.main),
          color: (theme) => theme.palette.success.contrastText,
          '&:hover': {
            backgroundColor: (theme) => (isHiglightedEvent ? theme.palette.secondary.dark : theme.palette.success.dark),
            boxShadow: (theme) => theme.shadows[4],
          },
        }}
        onClick={() => highlightEventClick(eventId)}
      >
        <EventAvailable fontSize="inherit" />
      </Fab>
    </GRTooltip>
  ) : null
}
